import { useState, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Plus, Edit, Trash2, Power, PowerOff, RefreshCw, Eye } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import { EmailTemplateModal } from './EmailTemplateModal'
import { EmailPreviewModal } from './EmailPreviewModal'

export interface EmailTemplate {
  id: string
  nome: string
  assunto: string
  corpo: string
  variaveis: string | null
  categoria: string
  setor: string
  systemKey: string | null
  descricao: string | null
  campos: string | null
  meta: string | null
  isSystem: boolean
  ativo: boolean
  createdAt: string
  updatedAt: string
}

const SETOR_LABELS: Record<string, string> = {
  bdr: 'Leads',
  financeiro: 'Financeiro',
  gestores: 'Gestores',
  autenticacao: 'Autenticação',
  solicitante: 'Solicitantes',
  portal_pj: 'Portal PJ',
}

const SETOR_ORDER = ['bdr', 'financeiro', 'gestores', 'solicitante', 'portal_pj', 'autenticacao']

function parseGatilho(template: EmailTemplate): string {
  if (!template.meta) return template.descricao ?? '—'
  try {
    const meta = JSON.parse(template.meta) as { gatilho?: string }
    return meta.gatilho ?? template.descricao ?? '—'
  } catch {
    return template.descricao ?? '—'
  }
}

function TemplateTable({
  items,
  onToggle,
  onEdit,
  onDelete,
  onPreview,
}: {
  items: EmailTemplate[]
  onToggle: (template: EmailTemplate) => void
  onEdit: (template: EmailTemplate) => void
  onDelete: (template: EmailTemplate) => void
  onPreview: (template: EmailTemplate) => void
}) {
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-[var(--glass-border)]">
          {['Nome', 'Gatilho', 'Assunto', 'Tipo', 'Envio', 'Ações'].map((h) => (
            <th
              key={h}
              className="text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap"
              style={{ fontSize: 'var(--font-size-label)' }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <AnimatePresence>
          {items.map((template) => {
            let camposCount = 0
            try {
              camposCount = template.campos ? JSON.parse(template.campos).length : 0
            } catch {
              camposCount = 0
            }
            return (
              <motion.tr
                key={template.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn(
                  'border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors',
                  !template.ativo && 'opacity-60',
                )}
              >
                <td className="px-4 py-3">
                  <div className="text-sm text-text-primary font-medium">{template.nome}</div>
                  {template.descricao ? (
                    <p className="mt-0.5 text-[11px] text-text-muted line-clamp-2">{template.descricao}</p>
                  ) : null}
                  {camposCount > 0 ? (
                    <p className="mt-0.5 text-[10px] text-text-muted">{camposCount} campo(s) no corpo</p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-[11px] text-text-muted max-w-[220px]">
                  <span className="line-clamp-3">{parseGatilho(template)}</span>
                </td>
                <td className="px-4 py-3 text-sm text-text-muted truncate max-w-[200px]">{template.assunto}</td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'px-2 py-0.5 rounded-full text-[11px] font-semibold border',
                      template.isSystem
                        ? 'bg-accent/10 text-accent border-accent/20'
                        : 'bg-white/[0.04] text-text-muted border-white/[0.06]',
                    )}
                  >
                    {template.isSystem ? 'Sistema' : 'Personalizado'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onToggle(template)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all min-h-[36px]',
                      template.ativo
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                        : 'bg-white/[0.04] text-text-muted border border-white/[0.06] hover:text-text-primary',
                    )}
                    title={template.ativo ? 'Clique para desativar envio' : 'Clique para ativar envio'}
                  >
                    {template.ativo ? <Power size={12} /> : <PowerOff size={12} />}
                    {template.ativo ? 'Ativo' : 'Desativado'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onPreview(template)}
                      className="p-2 rounded-lg hover:bg-white/[0.06] text-text-muted hover:text-sky-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title="Preview"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => onEdit(template)}
                      className="p-2 rounded-lg hover:bg-white/[0.06] text-text-muted hover:text-accent transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title="Editar"
                    >
                      <Edit size={14} />
                    </button>
                    {!template.isSystem ? (
                      <button
                        onClick={() => onDelete(template)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-text-muted hover:text-red-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        title="Remover"
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </div>
                </td>
              </motion.tr>
            )
          })}
        </AnimatePresence>
      </tbody>
    </table>
  )
}

export function EmailTemplatesTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null)
  const [activeSetor, setActiveSetor] = useState<string>('bdr')

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/email/templates', {
        params: { includeInactive: true },
      })
      setTemplates(data.data ?? [])
    } catch {
      toast.error('Erro ao carregar templates')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const handleSync = async () => {
    setSyncing(true)
    try {
      await api.post('/email/templates/sync-system')
      toast.success('Templates de sistema sincronizados')
      await fetchTemplates()
    } catch {
      toast.error('Erro ao sincronizar templates')
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async (template: EmailTemplate) => {
    if (template.isSystem) {
      toast.error('Templates de sistema não podem ser removidos')
      return
    }
    if (!confirm('Desativar este template?')) return
    try {
      await api.delete(`/email/templates/${template.id}`)
      toast.success('Template desativado')
      fetchTemplates()
    } catch {
      toast.error('Erro ao desativar template')
    }
  }

  const handleToggleAtivo = async (template: EmailTemplate) => {
    if (template.ativo) {
      const msg = template.isSystem
        ? `Desativar "${template.nome}"? O CRM deixará de enviar este e-mail automaticamente.`
        : `Desativar "${template.nome}"?`
      if (!confirm(msg)) return
    }

    try {
      await api.patch(`/email/templates/${template.id}`, { ativo: !template.ativo })
      toast.success(template.ativo ? 'E-mail desativado' : 'E-mail ativado')
      fetchTemplates()
    } catch {
      toast.error('Erro ao atualizar template')
    }
  }

  const handleSetorBulk = async (ativo: boolean) => {
    if (activeSetor === 'all') return
    const label = SETOR_LABELS[activeSetor] ?? activeSetor
    const action = ativo ? 'ativar' : 'desativar'
    if (!confirm(`${ativo ? 'Ativar' : 'Desativar'} todos os e-mails de ${label}?`)) return

    setBulkLoading(true)
    try {
      const { data } = await api.patch(`/email/templates/setor/${activeSetor}/ativo`, { ativo })
      toast.success(`${data.data?.updated ?? 0} e-mail(s) ${action === 'ativar' ? 'ativado(s)' : 'desativado(s)'}`)
      await fetchTemplates()
    } catch {
      toast.error(`Erro ao ${action} e-mails do setor`)
    } finally {
      setBulkLoading(false)
    }
  }

  const setorStats = useMemo(() => {
    const stats: Record<string, { total: number; ativos: number }> = {}
    for (const template of templates) {
      const key = template.setor || 'bdr'
      const current = stats[key] ?? { total: 0, ativos: 0 }
      current.total += 1
      if (template.ativo) current.ativos += 1
      stats[key] = current
    }
    return stats
  }, [templates])

  const activeCount = useMemo(() => templates.filter((t) => t.ativo).length, [templates])

  const groupedTemplates = useMemo(() => {
    const filtered =
      activeSetor === 'all' ? templates : templates.filter((t) => t.setor === activeSetor)

    if (activeSetor !== 'all') {
      return [{ setor: activeSetor, label: SETOR_LABELS[activeSetor] ?? activeSetor, items: filtered }]
    }

    const groups = new Map<string, EmailTemplate[]>()
    for (const template of filtered) {
      const key = template.setor || 'bdr'
      const list = groups.get(key) ?? []
      list.push(template)
      groups.set(key, list)
    }

    return SETOR_ORDER.filter((setor) => groups.has(setor)).map((setor) => ({
      setor,
      label: SETOR_LABELS[setor] ?? setor,
      items: groups.get(setor) ?? [],
    }))
  }, [templates, activeSetor])

  const currentSetorStats = useMemo(() => {
    if (activeSetor === 'all') {
      return { total: templates.length, ativos: activeCount }
    }
    return setorStats[activeSetor] ?? { total: 0, ativos: 0 }
  }, [activeSetor, setorStats, templates.length, activeCount])

  useEffect(() => {
    if (loading) return
    const current = setorStats[activeSetor]
    if (current && current.total > 0) return
    const firstWithTemplates = SETOR_ORDER.find((setor) => (setorStats[setor]?.total ?? 0) > 0)
    if (firstWithTemplates && firstWithTemplates !== activeSetor) {
      setActiveSetor(firstWithTemplates)
    }
  }, [loading, setorStats, activeSetor])

  return (
    <div className="glass-card p-0 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[var(--glass-border)]">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-accent" />
          <div>
            <h3
              className="font-semibold text-[var(--font-color-h3)]"
              style={{ fontSize: 'var(--font-size-h3)' }}
            >
              Templates de Email
            </h3>
            <p className="text-xs text-text-muted">
              {activeCount}/{templates.length} ativos — ative ou desative o envio por template ou por setor
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void handleSync()}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border border-[var(--glass-border)] text-text-muted hover:text-text-primary transition-all min-h-[44px]"
          >
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
            Sincronizar sistema
          </button>
          <button
            onClick={() => {
              setEditingTemplate(null)
              setShowModal(true)
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-accent text-white shadow-lg shadow-accent/20 hover:bg-accent/90 transition-all min-h-[44px]"
          >
            <Plus size={13} />
            Novo Template Leads
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-[var(--glass-border)] px-4 py-3">
        {SETOR_ORDER.map((setor) => {
          const stat = setorStats[setor] ?? { total: 0, ativos: 0 }
          if (stat.total === 0) return null
          return (
            <button
              key={setor}
              onClick={() => setActiveSetor(setor)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
                activeSetor === setor
                  ? 'bg-accent/15 text-accent border border-accent/30'
                  : 'bg-white/[0.04] text-text-muted border border-white/[0.06] hover:text-text-primary',
              )}
            >
              {SETOR_LABELS[setor]}
              <span className="ml-1 opacity-70">
                {stat.ativos}/{stat.total}
              </span>
            </button>
          )
        })}
      </div>

      {activeSetor !== 'all' && currentSetorStats.total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--glass-border)] bg-white/[0.02] px-4 py-2">
          <p className="text-xs text-text-muted">
            <span className="font-semibold text-text-primary">{SETOR_LABELS[activeSetor]}</span>
            {' — '}
            {currentSetorStats.ativos} de {currentSetorStats.total} e-mails ativos
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void handleSetorBulk(true)}
              disabled={bulkLoading || currentSetorStats.ativos === currentSetorStats.total}
              className="rounded-md border border-green-500/20 bg-green-500/10 px-2.5 py-1 text-[11px] font-semibold text-green-400 transition-colors hover:bg-green-500/20 disabled:opacity-40"
            >
              Ativar todos
            </button>
            <button
              onClick={() => void handleSetorBulk(false)}
              disabled={bulkLoading || currentSetorStats.ativos === 0}
              className="rounded-md border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[11px] font-semibold text-text-muted transition-colors hover:text-text-primary disabled:opacity-40"
            >
              Desativar todos
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : groupedTemplates.length === 0 || groupedTemplates.every((g) => g.items.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-12 text-text-muted">
            <Mail size={32} className="mb-3 opacity-30" />
            <p className="text-sm">Nenhum template cadastrado</p>
            <button
              onClick={() => void handleSync()}
              className="mt-3 text-xs text-accent hover:underline"
            >
              Sincronizar templates de sistema
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[var(--glass-border)]">
            {groupedTemplates.map((group) => (
              <section key={group.setor}>
                {activeSetor === 'all' ? (
                  <div className="flex items-center justify-between px-4 py-2 bg-white/[0.02]">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-text-label">
                      {group.label}
                    </h4>
                    <span className="text-[10px] text-text-muted">
                      {(setorStats[group.setor]?.ativos ?? 0)}/{(setorStats[group.setor]?.total ?? 0)} ativos
                    </span>
                  </div>
                ) : null}
                <TemplateTable
                  items={group.items}
                  onToggle={(t) => void handleToggleAtivo(t)}
                  onPreview={(t) => setPreviewTemplate(t)}
                  onEdit={(t) => {
                    setEditingTemplate(t)
                    setShowModal(true)
                  }}
                  onDelete={(t) => void handleDelete(t)}
                />
              </section>
            ))}
          </div>
        )}
      </div>

      <EmailTemplateModal
        open={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingTemplate(null)
        }}
        onSave={() => {
          setShowModal(false)
          setEditingTemplate(null)
          fetchTemplates()
        }}
        template={editingTemplate}
      />

      <EmailPreviewModal
        open={!!previewTemplate}
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
    </div>
  )
}
